import React from 'react';

import { apolloHooks } from '@nxte-nl/apollo-hooks';

import { getLocationAddressSuggestions } from '../../queries/getLocationAddressSuggestions.gql';
import { search as searchQuery } from '../../queries/search.gql';
import { search_v1 as searchV1Query } from '../../queries/search_v1.gql';
import { BaseResponse } from '../../types/apiCompoundCommon';
import {
  AddressSuggestion,
  CB_GivingMomentsPage,
  GiftcardProduct,
  GroupTypes,
  SearchGroup,
  SearchType,
} from '../../types/apiTypes';

type UseSearchLocationsParams = {
  filters: {
    keywords: string;
  };
  paging: {
    itemsPerPage: number;
    pageNumber: number;
  };
};

type UseSearchProductsParams = {
  inputText: string;
  type?: SearchType[];
};

type ProductsSearchGroups = Array<{
  groupType: GroupTypes;
  items: Array<CB_GivingMomentsPage | GiftcardProduct>;
}>;

export type SearchResponse = {
  body: ProductsSearchGroups;
};

export const searchHooks = {
  useGetLocationsStates() {
    const [searchResults, setSearchResults] = React.useState<AddressSuggestion[] | null>(null);
    return { searchResults, setSearchResults };
  },
  useGetProductsStates() {
    const [searchResults, setSearchResults] =
      React.useState<GiftcardProduct[] | null>(null);
    return { searchResults, setSearchResults };
  },
  useSearchLocations(
    searchText: string | null,
    paramsCallback: (searchText: string) => UseSearchLocationsParams,
  ) {
    const { searchResults, setSearchResults } = searchHooks.useGetLocationsStates();

    const { loading } = apolloHooks.useQuery(getLocationAddressSuggestions, {
      variables: React.useMemo(
        () => (searchText ? paramsCallback(searchText) : undefined),
        [paramsCallback, searchText],
      ),
      onCompleted: React.useCallback(
        (response: BaseResponse<AddressSuggestion[]>) => setSearchResults(response.data),
        [setSearchResults],
      ),
      skip: !searchText,
    });

    const isSearchInputEmpty = React.useMemo(() => !searchText, [searchText]);

    return { searchResults, loading, isSearchInputEmpty };
  },

  useMergeProducts(searchGroups: ProductsSearchGroups) {
    return searchGroups.reduce((
      acc: unknown[], cur: SearchGroup,
    ) => {
      if (cur && cur.items.length) {
        acc.push(...cur.items);
      }
      return acc;
    }, []);
  },

  useSearchProducts(
    searchText: string | null,
    paramsCallback: (searchText: string) => UseSearchProductsParams,
    isSearchV1: boolean,
    setCompleted: React.Dispatch<React.SetStateAction<boolean>>,
  ) {
    const { searchResults, setSearchResults } = searchHooks.useGetProductsStates();

    const query = isSearchV1 ? searchV1Query : searchQuery;

    const variables = React.useMemo(() => {
      return searchText ? paramsCallback(searchText) : undefined;
    }, [paramsCallback, searchText]);

    const onCompleted = React.useCallback(
      (response: BaseResponse<SearchResponse | GiftcardProduct[]>) => {
        const data = isSearchV1
          ? searchHooks.useMergeProducts((response as BaseResponse<SearchResponse>).data.body)
          : response.data;
        setSearchResults(data as GiftcardProduct[]);
        setCompleted(true);
      },
      [isSearchV1, setSearchResults, setCompleted],
    );

    const { loading } = apolloHooks.useQuery(query, {
      variables,
      onCompleted,
      skip: !searchText,
    });

    const isSearchInputEmpty = React.useMemo(() => !searchText, [searchText]);

    return { searchResults, loading, isSearchInputEmpty };
  },
};
